"use client";

import CardGrid from "@/components/card-grid";
import { Button } from "@/components/shadcn/button";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/shadcn/tabs";
import { useRouter } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import ClassCard, { ClassCardData } from "./class-card";

type ClassTab = "joined" | "created";

export default function ClassesClientWrapper({
    studentClasses,
    instructorClasses,
}: {
    studentClasses: ClassCardData[];
    instructorClasses: ClassCardData[];
}) {
    const t = useTranslations("classes");
    const router = useRouter();
    const searchParams = useSearchParams();
    const [activeTab, setActiveTab] = useState<ClassTab>("created");

    useEffect(() => {
        const tabParam = searchParams.get("tab");
        if (tabParam === "joined" || tabParam === "created") {
            setActiveTab(tabParam);
        }
    }, [searchParams]);

    const handleTabChange = (value: string) => {
        const newTab = value as ClassTab;
        setActiveTab(newTab);
        const url = new URL(window.location.href);
        url.searchParams.set("tab", newTab);
        router.replace(url.pathname + url.search, { scroll: false });
    };

    const handleClassLeft = () => {
        router.refresh();
    };

    const hasCreatedClass = instructorClasses.length > 0;

    return (
        <Tabs
            value={activeTab}
            className="w-full"
            onValueChange={handleTabChange}
        >
            <div className="mb-4 flex items-center justify-between">
                <TabsList>
                    <TabsTrigger value="created">{t("myClasses")}</TabsTrigger>
                    <TabsTrigger value="joined">
                        {t("joinedClasses")}
                    </TabsTrigger>
                </TabsList>
                {activeTab === "created" && hasCreatedClass && (
                    <CreateClassButton />
                )}
            </div>

            <TabsContent value="joined" className="mt-6">
                {studentClasses.length > 0 ? (
                    <CardGrid>
                        {studentClasses.map((classData) => (
                            <ClassCard
                                key={classData.id}
                                classData={classData}
                                isStudent={true}
                                onLeaveClass={handleClassLeft}
                            />
                        ))}
                    </CardGrid>
                ) : (
                    <EmptyState message={t("emptyJoined")} />
                )}
            </TabsContent>

            <TabsContent value="created" className="mt-6">
                {hasCreatedClass ? (
                    <CardGrid>
                        {instructorClasses.map((classData) => (
                            <ClassCard
                                key={classData.id}
                                className="cursor-pointer transition-shadow hover:shadow-md"
                                classData={classData}
                                onClick={() =>
                                    router.push(
                                        `/kullanici/siniflar/${classData.id}`,
                                    )
                                }
                            />
                        ))}
                    </CardGrid>
                ) : (
                    <>
                        <EmptyState message={t("emptyCreated")} />
                        <CreateClassButton />
                    </>
                )}
            </TabsContent>
        </Tabs>
    );
}

function CreateClassButton() {
    const t = useTranslations("classes");
    const router = useRouter();

    return (
        <Button
            onClick={() => router.push("/kullanici/siniflar/olustur")}
            className="h-10"
        >
            {t("createClassButton")}
        </Button>
    );
}

function EmptyState({ message }: { message: string }) {
    return (
        <div className="flex flex-col items-start justify-center gap-2 py-4">
            <img
                src="/images/empty-list.png"
                alt="illustration for an empty list"
            />
            <p className="text-center text-gray-500">{message}</p>
        </div>
    );
}
